import { RealtimeModes as modes } from "mobility-toolbox-js/api";
import { getHeight } from "ol/extent";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import { unByKey } from "ol/Observable";
import VectorSource from "ol/source/Vector";
import { Icon, Stroke, Style, Text } from "ol/style";
import { memo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  NOTIFICATION_SOURCE_LAYERS_COLLISION_SCHEMATIC,
  NOTIFICATION_STYLE_LAYERS_COLLISION_TOPOGRAPHIC,
  NOTIFICATIONS_SOURCE_ID,
} from "../constants";
import { setNotificationsIconsLayer } from "../model/actions";
import { getUniqueFeaturesCollection } from "../utils";
import findNotificationIconCoordinate from "../utils/findNotificationIconCoordinate";
import getIconNameFromNotification from "../utils/getIconNameFromNotification";

const styleCache = {};
const iconLayer = new VectorLayer({
  source: new VectorSource(),
});

function NotificationIconLayer() {
  const dispatch = useDispatch();
  const debug = useSelector((state) => state.isDefaultDebug);
  const map = useSelector((state) => state.map);
  const mode = useSelector((state) => state.mode);
  const topographicBgLayer = useSelector((state) => state.topographicBgLayer);
  const schematicBgLayer = useSelector((state) => state.schematicBgLayer);
  const backgroundLayer = useSelector((state) => state.backgroundLayer);
  const notificationsLayer = useSelector((state) => state.notificationsLayer);

  const layerStyleFunction = useCallback(
    (feature, resolution) => {
      const { affectedTimeIntervalsStart, category, isActive, isPublished } =
        feature.getProperties();
      const icon = getIconNameFromNotification(feature);
      let src = `/static/${icon}.png`;
      let text = "";

      if (!isActive && affectedTimeIntervalsStart) {
        src = `/static/${icon}-banner.png`;
        text = `ab ${new Date(affectedTimeIntervalsStart).toLocaleDateString(
          [],
          {
            day: "2-digit",
            month: "2-digit",
          },
        )}`;
      }
      let scale = Math.max(250 / resolution, isActive ? 0.14 : 0.17);
      scale = Math.min(scale, 0.25);
      let scaleText = Math.max(160 / resolution, 1.3);
      scaleText = Math.min(scaleText, 1.6);
      const key = `${src}-${text}-${isActive}-${scale}-${scaleText}${
        debug ? "-debug" : ""
      }}`;
      if (!styleCache[key]) {
        styleCache[key] = new Style({
          image: new Icon({
            opacity: 0.8,
            scale,
            src,
          }),
          // Only for debug purpose
          stroke: debug
            ? new Stroke({
                color: [255, 0, 0, 0.3],
                width: 4,
              })
            : undefined,
          text: new Text({
            font: "bold 9px sans-serif",
            offsetX: -15,
            scale: scaleText,
            text,
            textAlign: "left",
          }),
        });
      }
      return styleCache[key];
    },
    [debug],
  );

  useEffect(() => {
    dispatch(setNotificationsIconsLayer(iconLayer));
  }, [dispatch]);

  // Update style
  useEffect(() => {
    iconLayer.setStyle(layerStyleFunction);
  }, [layerStyleFunction]);

  // Adds/removes layer
  useEffect(() => {
    map?.addLayer(iconLayer);

    return () => {
      map?.removeLayer(iconLayer);
    };
  }, [map]);

  // Update icon position
  const updateIconPosition = useCallback(
    (modee, bgLayer) => {
      const mbMap = bgLayer?.mbMap;
      if (!mbMap?.getStyle()?.layers) {
        return;
      }
      // To place the notification icon without hiding other elements,
      // we have to test if it collides with other layer.
      let layersToTestCollision =
        NOTIFICATION_STYLE_LAYERS_COLLISION_TOPOGRAPHIC;

      if (modee === modes.SCHEMATIC) {
        layersToTestCollision = mbMap
          .getStyle()
          ?.layers.filter((layer) => {
            return NOTIFICATION_SOURCE_LAYERS_COLLISION_SCHEMATIC.includes(
              layer["source-layer"] || layer.source,
            );
          })
          .map(({ id }) => id);
      }
      const layers = mbMap
        .getStyle()
        .layers.filter((layer) => {
          return layer.source === NOTIFICATIONS_SOURCE_ID;
        })
        .map(({ id }) => id);
      let notificationsDisplayed = [];
      if (layers.length) {
        notificationsDisplayed = getUniqueFeaturesCollection(
          mbMap.queryRenderedFeatures({
            layers,
          }),
          "situationId",
          {
            featureProjection: map.getView().getProjection(),
          },
        );
      }

      const source = iconLayer.getSource();
      // Remove all debug features
      source
        .getFeatures()
        .filter((f) => !f.getId())
        .forEach((f) => {
          source.removeFeature(f);
        });

      // Add  all notifications icons
      notificationsDisplayed.forEach((notification) => {
        let situationId;
        try {
          situationId = notification.get("situationId");
        } catch (e) {
          console.error("Error in updateIconPosition", e);
          return;
        }

        if (debug) {
          source.addFeature(notification);
        }

        // Get the coordinate of the notification icon
        let coordinate = findNotificationIconCoordinate(
          situationId,
          notification,
          iconLayer,
          bgLayer,
          debug,
          {
            layers: layersToTestCollision,
          },
        );

        // Remove the previous icon.
        const prevIconFeature = source.getFeatureById(situationId);
        if (prevIconFeature) {
          source.removeFeature(prevIconFeature);
        }

        // Add the icon corresponding to this situation, if we found a coordinate.
        if (coordinate) {
          const iconFeature = notification.clone();
          iconFeature.setId(situationId);
          iconFeature.setGeometry(new Point(coordinate));
          source.addFeature(iconFeature);
        }
      });
    },
    [debug, map],
  );

  // Update icon position on map moveend and when the moco source is updated
  useEffect(() => {
    const mbMap = backgroundLayer?.mbMap;

    if (!map || !mbMap) {
      return;
    }

    // Update icon position on each moveeend
    const onMapMoved = () => {
      const mapExtent = map.getView().calculateExtent();
      if (getHeight(mapExtent) <= 0) {
        return;
      }
      updateIconPosition(mode, backgroundLayer);
    };

    onMapMoved();
    const key = map.on("moveend", onMapMoved);

    const onSourceData = (evt) => {
      if (evt.sourceId === "moco" || evt.isSourceLoaded) {
        updateIconPosition(mode, backgroundLayer);
      }
    };

    mbMap.on("sourcedata", onSourceData);

    return () => {
      unByKey(key);
      mbMap.off("sourcedata", onSourceData);
    };
  }, [
    debug,
    mode,
    map,
    updateIconPosition,
    backgroundLayer?.mbMap,
    backgroundLayer,
  ]);

  return null;
}
export default memo(NotificationIconLayer);
